import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Box,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Line, LineChart } from 'recharts';
import axios from 'axios';
import { BarChart3, Table2, Eye, RefreshCw } from 'lucide-react';
import FieldHelp from '../ui/FieldHelp';
import { LEGACY_DASHBOARD_FIELD_HELP as HELP } from './dashboardFieldHelp';
import StudentDetailsDialog from './StudentDetailsDialog';
import { useCourseList } from '../hooks/useCourseList';
import { Card, Btn, SectionHead, SetTabs, SetTab, Tag } from '../ui/primitives';

/** How often the selected assessment's results are re-fetched in the background. */
const REFRESH_INTERVAL_MS = 30000;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.gap};
  margin-bottom: ${({ theme }) => theme.gap};
`;

const ChartCard = styled(Card)`
  padding: 16px;
  margin-top: ${({ theme }) => theme.gap};

  h4 {
    margin: 0 0 12px;
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 14px;
    font-weight: 750;
    color: ${({ theme }) => theme.text};
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin-top: 12px;
`;

const ResultTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    padding: 9px 12px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
    text-align: left;
    white-space: nowrap;
  }

  th {
    color: ${({ theme }) => theme.textSubtle};
    font-weight: 700;
    font-size: ${({ theme }) => theme.fsMicro};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  tbody tr:hover td {
    background: ${({ theme }) => theme.surface2};
  }

  tr.is-empty td {
    color: ${({ theme }) => theme.textSubtle};
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Stamp = styled.span`
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textSubtle};
`;

const EmptyNote = styled.p`
  margin: 14px 2px 0;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
`;

const CourseAssessmentSelector = ({ course }) => {
  const { courses: courseOptions } = useCourseList();
  const [selectedCourse, setSelectedCourse] = useState('');
  const [courses, setCourses] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState('');
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [students, setStudents] = useState([]);
  const [groupedStudents, setGroupedStudents] = useState({});
  const [selectedStudent, setSelectedStudent] = useState('');
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [viewMode, setViewMode] = useState('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedStudent1, setSelectedStudent1] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  // Background refresh state — `refreshing` drives the subtle spinner on the
  // Refresh button without blanking the table the way `loadingStudents` does.
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const theme = useTheme();
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const apiUrl = process.env.REACT_APP_API_URL;

  const authHeader = {
    headers: {
      Authorization: `Basic ${btoa(`${username}:${password}`)}`,
    },
  };

  // ✅ Safe date formatting helper
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(Number(timestamp));
    return isNaN(date) ? 'N/A' : date.toLocaleDateString('en-GB'); // dd/mm/yyyy
  };

  // ✅ Sort all students based on createdAt timestamp
  const sortedStudents = [...students].sort((a, b) => {
    const timeA = a.createdAt ? Number(a.createdAt) : 0;
    const timeB = b.createdAt ? Number(b.createdAt) : 0;
    return sortOrder === 'asc' ? timeA - timeB : timeB - timeA;
  });

  // The course list can come from a parent (`course` prop) or, when rendered
  // standalone, from the shared instructor course list. Guarded so a missing or
  // malformed prop leaves `courses` an array rather than undefined.
  useEffect(() => {
    if (Array.isArray(course) && course.length) {
      setCourses(course);
      return;
    }
    setCourses(
      (courseOptions || []).map((c) => ({ courseId: c.id, courseName: c.name }))
    );
  }, [course, courseOptions]);

  const handleViewDetails = ({ email, attempts }) => {
    const student = attempts;
    setSelectedStudent1(student);
    setDetailsDialogOpen(true);
  };

  useEffect(() => {
    const fetchAssessments = async () => {
      if (!selectedCourse) return;
      try {
        setLoadingAssessments(true);
        const response = await axios.get(`${apiUrl}/api/Assessment/findCourse/${selectedCourse}`, authHeader);
        setAssessments(response.data || []);
        setSelectedAssessment('');
        setStudents([]);
        setSelectedStudent('');
        setGroupedStudents({});
      } catch (error) {
        console.error('Error fetching assessments:', error);
        setAssessments([]);
      } finally {
        setLoadingAssessments(false);
      }
    };

    fetchAssessments();
  }, [selectedCourse]);

  /**
   * Loads the selected assessment's results.
   *
   * `silent` is used by the background refresh: it leaves the current table and
   * charts on screen (no spinner, no reset of the chosen student) and simply swaps
   * the data in, so live updates never interrupt what the user is reading.
   */
  const fetchStudents = useCallback(async ({ silent = false } = {}) => {
    if (!selectedAssessment) return;
    try {
      if (silent) setRefreshing(true);
      else setLoadingStudents(true);
      const response = await axios.get(`${apiUrl}/api/testResult/${selectedAssessment}`, authHeader);
      const studentData = response.data?.data || [];
      setStudents(studentData);
      if (!silent) setSelectedStudent('');
      groupAndCalculate(studentData);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching students:', error);
      // A failed background poll keeps the data already on screen.
      if (!silent) {
        setStudents([]);
        setGroupedStudents({});
      }
    } finally {
      if (silent) setRefreshing(false);
      else setLoadingStudents(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssessment, apiUrl]);

  // Held in a ref so the poll timer below is created once per assessment rather
  // than being torn down and rebuilt whenever the callback identity changes.
  const fetchStudentsRef = useRef(fetchStudents);
  useEffect(() => {
    fetchStudentsRef.current = fetchStudents;
  }, [fetchStudents]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Live updates: poll while an assessment is selected, and refresh immediately
  // when the tab regains focus so returning to the page never shows stale results.
  useEffect(() => {
    if (!selectedAssessment) return undefined;

    const refresh = () => {
      if (document.visibilityState === 'visible') fetchStudentsRef.current({ silent: true });
    };
    const timer = setInterval(refresh, REFRESH_INTERVAL_MS);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);

    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [selectedAssessment]);

  /** An attempt's score as a percentage, or null when it can't be computed. */
  const toPercent = (attempt) => {
    const total = Number(attempt?.totalMarks);
    const score = Number(attempt?.score);
    if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(score)) return null;
    return parseFloat(((score / total) * 100).toFixed(2));
  };

  /**
   * Line-chart series, derived from the loaded results rather than held in state.
   *
   * `average` is the group's mean percentage **at that attempt number** — not one
   * flat overall figure — so attempt 1 is compared with attempt 1. `student` is the
   * selected learner's percentage for the same attempt. Deriving it means a
   * background refresh can never drop the student series or leave it stale.
   */
  const graphData = useMemo(() => {
    const learners = Object.values(groupedStudents);
    if (!learners.length) return [];

    const maxTests = learners.reduce((max, g) => Math.max(max, g.testResults.length), 0);
    const selected = selectedStudent ? groupedStudents[selectedStudent] : null;

    return Array.from({ length: maxTests }, (_, i) => {
      // Only learners who actually made this attempt count toward its average.
      const values = learners
        .map((g) => g.testResults[i]?.percent)
        .filter((v) => v != null);
      const average = values.length
        ? parseFloat((values.reduce((a, b) => a + b, 0) / values.length).toFixed(2))
        : null;

      const point = { name: `Attempt ${i + 1}`, average };
      if (selected) point.student = selected.testResults[i]?.percent ?? null;
      return point;
    });
  }, [groupedStudents, selectedStudent]);

  const groupAndCalculate = (studentsData) => {
    const grouped = {};
    studentsData.forEach((s) => {
      if (!grouped[s.studentId]) {
        grouped[s.studentId] = { ...s, testResults: [] };
      }
      grouped[s.studentId].testResults.push({
        score: parseFloat(s.score),
        // The chart's Y axis is 0-100, so attempts are compared as percentages —
        // a raw mark (e.g. 8 of 10) would otherwise plot near the bottom.
        percent: toPercent(s),
        createdAt: s.createdAt,
      });
    });

    // Order each learner's attempts oldest → newest so "Attempt N" lines up across
    // learners; the API does not guarantee an order.
    Object.values(grouped).forEach((g) =>
      g.testResults.sort((a, b) => (Number(a.createdAt) || 0) - (Number(b.createdAt) || 0))
    );

    setGroupedStudents(grouped);
  };

  const renderBarChart = () => {
    const domainStats = {};
  
    // Step 1: Group performance (average correct answers per domain across all students and attempts)
    Object.values(students).forEach((attempts) => {
      const attemptList = Array.isArray(attempts) ? attempts : [attempts];

      attemptList.forEach((attempt) => {
        attempt?.domainPerformance?.forEach((d) => {
          const domain = d.domainName;
          const correct = parseInt(d.correctAnswers) || 0;
  
          if (!domainStats[domain]) {
            domainStats[domain] = { totalCorrect: 0, count: 0 };
          }
  
          domainStats[domain].totalCorrect += correct;
          domainStats[domain].count += 1;
        });
      });
    });
  
    const domainLabels = Object.keys(domainStats);
    const avgCorrectAnswers = domainLabels.map((domain) => {
      const stats = domainStats[domain];
      return stats.count > 0
        ? parseFloat((stats.totalCorrect / stats.count).toFixed(2))
        : 0;
    });
  
    let chartData = domainLabels.map((domain, i) => ({
      domain,
      groupCorrect: avgCorrectAnswers[i],
    }));
  
    // Step 2: Selected student average per domain (across ALL their attempts)
    if (selectedStudent) {
      const studentDomainTotals = {};
  
      // Flatten all attempts from all groups
      const allAttempts = Object.values(students).flatMap((attempts) =>
        Array.isArray(attempts) ? attempts : [attempts]
      );
  
      // Filter only attempts by selectedStudent
      const studentAttempts = allAttempts.filter(
        (attempt) => attempt.studentId === selectedStudent
      );

      // Aggregate correct answers by domain across attempts for selected student
      studentAttempts.forEach((attempt) => {
        attempt?.domainPerformance?.forEach((d) => {
          const domain = d.domainName;
          const correct = parseInt(d.correctAnswers) || 0;
  
          if (!studentDomainTotals[domain]) {
            studentDomainTotals[domain] = { totalCorrect: 0, count: 0 };
          }
  
          studentDomainTotals[domain].totalCorrect += correct;
          studentDomainTotals[domain].count += 1;
        });
      });
  
      // Merge into chartData for the selected student. A domain the learner never
      // attempted is null, not 0 — Recharts then draws no bar, instead of one that
      // reads as "scored zero".
      chartData = domainLabels.map((domain, i) => ({
        domain,
        groupCorrect: avgCorrectAnswers[i],
        studentCorrect: studentDomainTotals[domain]
          ? parseFloat(
              (
                studentDomainTotals[domain].totalCorrect /
                studentDomainTotals[domain].count
              ).toFixed(2)
            )
          : null,
      }));
    }
  
    return (
      <ChartCard>
        <h4>
          {selectedStudent
            ? 'Student vs group performance by domain'
            : 'Group performance by domain'}
        </h4>
        <ResponsiveContainer width="100%" height={330}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
            <XAxis dataKey="domain" stroke={theme.textSubtle} fontSize={12} />
            <YAxis allowDecimals={false} stroke={theme.textSubtle} fontSize={12} />
            <RechartsTooltip />
            <Legend />
            <Bar
              dataKey="groupCorrect"
              fill={theme.accent}
              name="Group Avg Correct"
              radius={[4, 4, 0, 0]}
            />
            {selectedStudent && (
              <Bar
                dataKey="studentCorrect"
                fill={theme.accentStrong}
                name="Student Avg Correct"
                radius={[4, 4, 0, 0]}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    );
  };
  
  const selectSx = {
    borderRadius: theme.rMd,
    '& .MuiOutlinedInput-root': {
      minHeight: theme.controlH,
      borderRadius: theme.rMd,
      background: theme.surface,
    },
  };

  return (
    <div>
      <Card $pad>
        <SectionHead style={{ marginTop: 0 }}>
          <div>
            <h2>Course assessment analytics</h2>
            <p>Scores by domain and attempt for a selected assessment. Updates automatically.</p>
          </div>
          <Toolbar>
            {lastUpdated && (
              <Stamp>
                Updated {lastUpdated.toLocaleTimeString()}
                {refreshing ? ' · refreshing…' : ''}
              </Stamp>
            )}
            <Btn
              $variant="ghost"
              onClick={() => fetchStudents({ silent: true })}
              disabled={!selectedAssessment || refreshing}
            >
              <RefreshCw size={14} /> Refresh
            </Btn>
          </Toolbar>
        </SectionHead>

        <FilterGrid>
          {/* Course Dropdown */}
          <div>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                Select Course
                <FieldHelp text={HELP.selectCourseAssessments} />
              </InputLabel>
              <Select
                value={selectedCourse}
                label="Select Course"
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  setSelectedAssessment('');
                  setStudents([]);
                  setSelectedStudent('');
                  setGroupedStudents({});
                }}
              >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(courses || []).map((course) => (
                    <MenuItem key={course.courseId} value={course.courseId}>
                      {course.courseName}
                    </MenuItem>
                  ))}
                </Select>
            </FormControl>
          </div>

          {/* Assessment Dropdown */}
          <div>
            <FormControl fullWidth size="small" sx={selectSx} disabled={!selectedCourse}>
              <InputLabel>Select Assessment</InputLabel>
              <Select
                value={selectedAssessment}
                label="Select Assessment"
                onChange={(e) => setSelectedAssessment(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {loadingAssessments ? (
                  <MenuItem disabled>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} />
                      Loading...
                    </Box>
                  </MenuItem>
                ) : (assessments || []).length > 0 ? (
                  (assessments || []).map((assessment) => (
                    <MenuItem key={assessment.testId} value={assessment.testId}>
                      {assessment.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No assessments found</MenuItem>
                )}
              </Select>
            </FormControl>
          </div>

          {/* Student Dropdown */}
          <div>
            <FormControl fullWidth size="small" sx={selectSx} disabled={!selectedAssessment}>
              <InputLabel>Select Student</InputLabel>
              <Select
                value={selectedStudent}
                label="Select Student"
                onChange={(e) => setSelectedStudent(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {loadingStudents ? (
                  <MenuItem disabled>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} />
                      Loading...
                    </Box>
                  </MenuItem>
                ) : Object.keys(groupedStudents).length > 0 ? (
                  Object.values(groupedStudents).map((student) => (
                    <MenuItem key={student.studentId} value={student.studentId}>
                      {student.firstName} {student.lastName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No students found</MenuItem>
                )}
              </Select>
            </FormControl>
          </div>
        </FilterGrid>

        {!selectedAssessment && (
          <EmptyNote>Select a course and assessment to see its analytics.</EmptyNote>
        )}

        {selectedAssessment && !loadingStudents && students.length === 0 && (
          <EmptyNote>No results submitted for this assessment yet.</EmptyNote>
        )}

        {/* View mode */}
        {students.length > 0 && (
          <SetTabs style={{ marginTop: 4 }}>
            <SetTab $on={viewMode === 'graph'} onClick={() => setViewMode('graph')}>
              <BarChart3 size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
              Graph
            </SetTab>
            <SetTab $on={viewMode === 'table'} onClick={() => setViewMode('table')}>
              <Table2 size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
              Table
            </SetTab>
          </SetTabs>
        )}
  
        {/* Result Display */}
        <div>
        {students.length > 0 && viewMode === 'graph' && (
          <ChartCard>
            <h4>Performance by attempt</h4>
            <ResponsiveContainer width="100%" height={330}>
              <LineChart data={graphData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                <XAxis dataKey="name" stroke={theme.textSubtle} fontSize={12} />
                <YAxis domain={[0, 100]} stroke={theme.textSubtle} fontSize={12} />
                <RechartsTooltip />
                <Legend />
                <Line type="monotone" dataKey="average" stroke={theme.accent} strokeWidth={2} name="Average Score" />
                {selectedStudent && (
                  <Line type="monotone" dataKey="student" stroke={theme.accentStrong} strokeWidth={3} name="Student Score" />
                )}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
        {students.length > 0 && viewMode === 'graph' && renderBarChart()}
        {students.length > 0 && viewMode === 'table' && (
          <ChartCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h4 style={{ margin: 0 }}>Results</h4>
              <Btn
                $variant="ghost"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              >
                Time: {sortOrder === 'asc' ? 'Oldest first' : 'Newest first'}
              </Btn>
            </div>

            {/* Wrapper to enable horizontal scroll on small screens */}
            <TableWrap>
                  <ResultTable>
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Email</th>
                        <th>Attempts</th>
                        <th>Total Questions</th>
                        <th>Attempted</th>
                        <th>Correct</th>
                        <th>Score (%)</th>
                        <th>Time</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(
                        sortedStudents.reduce((acc, s) => {
                          const key = s.studentEmail;
                          acc[key] = acc[key] || [];
                          acc[key].push(s);
                          return acc;
                        }, {})
                      ).map(([email, allAttempts], idx) => {
                        // ✅ Filter out attempts with totalMarks = 0 or null
                        const validAttempts = allAttempts.filter(
                          (attempt) => attempt.totalMarks && Number(attempt.totalMarks) > 0
                        );

                        const totalAttempts = validAttempts.length;

                        // ✅ If no valid attempts, show one "N/A" row for that student
                        const displayAttempts = totalAttempts > 0 ? validAttempts : [allAttempts[0]];

                        return displayAttempts.map((attempt, i) => {
                          const percentage =
                            attempt.totalMarks && Number(attempt.totalMarks) > 0
                              ? ((attempt.score / attempt.totalMarks) * 100).toFixed(2)
                              : 'N/A';

                          return (
                            <tr
                              key={`${email}-${i}`}
                              className={totalAttempts === 0 ? 'is-empty' : undefined}
                            >
                              {i === 0 && (
                                <>
                                  <td rowSpan={displayAttempts.length}>
                                    {attempt.firstName || 'N/A'} {attempt.lastName || ''}
                                  </td>
                                  <td rowSpan={displayAttempts.length}>
                                    {email || 'N/A'}
                                  </td>
                                  <td rowSpan={displayAttempts.length}>
                                    {totalAttempts}
                                  </td>
                                </>
                              )}
                              <td>{attempt.totalQuestions || 'N/A'}</td>
                              <td>{attempt.attemptedQuestions || 'N/A'}</td>
                              <td>{attempt.correctAnswers || 'N/A'}</td>
                              <td>
                                {percentage === 'N/A'
                                  ? <Tag $tone="gray">N/A</Tag>
                                  : `${percentage}%`}
                              </td>
                              <td>{formatDate(attempt.createdAt)}</td>
                              {i === 0 && (
                                <td rowSpan={displayAttempts.length} style={{ textAlign: 'right' }}>
                                  <Btn
                                    $variant="ghost"
                                    onClick={() => handleViewDetails({ email, attempts: displayAttempts })}
                                  >
                                    <Eye size={14} /> View
                                  </Btn>
                                </td>
                              )}
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </ResultTable>
            </TableWrap>
          </ChartCard>
        )}
        </div>
      </Card>
      <StudentDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        students={selectedStudent1}
      />
    </div>
  );
  
};

export default CourseAssessmentSelector;
